import { useRouter } from 'expo-router';
import { Paragraph, YStack } from 'tamagui';
import { useQueryClient } from '@tanstack/react-query';

import { Heading } from '@/components/heading';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ScreenContainer } from '@/components/screen-container';
import { queryKeys } from '@/lib/query-keys';
import { markSafetyReminderSeen } from '@/lib/safety-reminder-seen';
import { borderWidth } from '@/theme/tokens.generated';

// Shown at the EXACT moment of mutual accept -- issue #16's own hard
// requirement, sourced from design.md's Trust & safety section:
// "the reveal-accept screen must carry a visible, explicit safety
// reminder (meet in public the first time, tell someone where you're
// going) at the moment of mutual accept, not rely on correct backend
// behavior to substitute for a user actually feeling safe." This
// screen is the only place this copy appears -- it is not duplicated
// in a settings page as a substitute for showing it here, and "Got it"
// is the only way off the screen (no back button, no header close, no
// swipe-to-dismiss route) so it can't be skipped unread. Wired as the
// Accept mutation's onSuccess navigation target in Task 6
// (screens/reveal-requests/index.tsx's IncomingRequestRow).
//
// connectionId is present only when the REQUESTER reaches this screen
// via reveal-requests/index.tsx's "New connection" prompt (issue #103):
// the recipient's route above is already a true one-shot event (the
// accept mutation itself), so it never needs to suppress a repeat
// showing and never passes this. When present, "Got it" records that
// this device has shown the reminder for this Connection (client-side
// presentation state only, per design.md's Design Principle 5 -- the
// safety enforcement itself is the printed copy, not this bookkeeping).
export function SafetyReminder({
  otherUserId,
  connectionId,
}: {
  otherUserId: string | undefined;
  connectionId?: string;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();

  return (
    <ScreenContainer>
      <YStack flex={1} pt="$8" gap="$6" alignItems="center">
        {otherUserId && <PseudonymousAvatar id={otherUserId} size={56} />}

        <Heading textAlign="center">You&apos;re connected</Heading>

        {/* Not color-only: the "Before you meet" label and the body
            copy itself carry the meaning -- the warning tint is a
            reinforcement, not the only signal, per this project's own
            "no color-only" accessibility rule. */}
        <YStack
          backgroundColor="$feedbackWarningBg"
          borderColor="$feedbackWarningBorder"
          borderWidth={borderWidth.hairline}
          borderRadius="$sm"
          p="$4"
          gap="$3"
          width="100%"
        >
          <Paragraph color="$feedbackWarningText" fontWeight="$semibold">
            Before you meet
          </Paragraph>
          <Paragraph color="$feedbackWarningText">
            Meet in a public place the first time. Tell a friend or family member where you&apos;re going and who
            you&apos;re meeting.
          </Paragraph>
        </YStack>

        <PrimaryButton
          onPress={() => {
            // Fire-and-forget, not awaited (issue #126 AC2): this write
            // is client-side presentation bookkeeping only (see this
            // file's own doc comment), so a slow or failed AsyncStorage
            // write must never delay or dead-end the "Got it" button --
            // the user has already read the reminder, the button's job
            // is to move them on. .catch keeps a storage failure from
            // becoming an unhandled promise rejection; the only visible
            // effect of a failure is the reminder possibly showing again
            // next time, not a stuck button.
            if (connectionId) {
              markSafetyReminderSeen(connectionId)
                .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.safetyReminderSeen() }))
                .catch(() => {});
            }
            router.push('/reveal-requests');
          }}
        >
          Got it
        </PrimaryButton>
      </YStack>
    </ScreenContainer>
  );
}
